/* eslint-disable react/no-array-index-key */
import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import IconMC from '../../../components/IconMC';

/**
 * ConversationFeatureIconsMC - Molecule Component
 *
 * Displays feature icons for conversation (bot, flow, automation, order)
 * Optimized with useMemo for render performance.
 *
 * @param {Object} conversation - Conversation object with feature flags
 * @param {boolean} conversation.hasMia - Has bot feature
 * @param {boolean} conversation.hasFlow - Has flow feature  
 * @param {boolean} conversation.hasDeals - Has deals feature
 * @param {boolean} conversation.idInShops - Has order feature
 */
const ConversationFeatureIconsMC = memo(({ conversation }) => {
  const { t } = useTranslation('InboxMC');

  // Memoiza la lista de features visibles
  const visibleFeatures = useMemo(() => [
    conversation.hasMia && {
      tooltip: t('conversation.features.mia'),
      icon: <IconMC name="bot" />
    },
    conversation.hasFlow && {
      tooltip: t('conversation.features.flows'),
      icon: <IconMC name="carbon-flow" />
    },
    conversation.hasDeals && {
      tooltip: t('conversation.features.deals'),
      icon: <IconMC name="briefcase-outline" />
    },
    conversation.hasShopOrder && {
      tooltip: t('conversation.features.orders'),
      icon: <IconMC name="shopping-cart-outline" />
    }
  ].filter(Boolean), [
    conversation.hasMia,
    conversation.hasFlow,
    conversation.hasDeals,
    conversation.hasShopOrder,
    t
  ]);

  if (visibleFeatures.length === 0) return null;

  return (
    <div className="tw-wrapper tw-flex tw-items-center tw-gap-1.5 tw-mb-2">
      {visibleFeatures.map((feature, index) => (
        <div
          key={index}
          className="tw-w-4 tw-h-4 tw-text-gray-700 feature-icon-tooltip"
          data-tooltip-content={feature.tooltip}
        >
          {feature.icon}
        </div>
      ))}
      <ReactTooltip
        place="top"
        anchorSelect=".feature-icon-tooltip"
        className="tw-z-50"
      />
    </div>
  );
});

ConversationFeatureIconsMC.displayName = 'ConversationFeatureIconsMC';

export default ConversationFeatureIconsMC;
